import ky from 'ky';

import {
  AiContext,
  GeneralSettingsPayload,
  InferenceMetaFieldsResponse,
  InvitedUser,
  Project,
  ProjectCreationResponse,
  ProjectMetadataPayload,
  ProjectMetadataResponse,
  Tag,
  User,
} from '~redux/types/user';

import {UserRole} from 'src/contexts/AbilitiesContext';
import {getOptions} from './api';

const AUTH_API = import.meta.env.VITE_AUTH_API ?? '';

export const createProject = ({
  name,
  description,
  aiContext,
}: {
  name: string;
  description: string;
  aiContext?: AiContext;
}) =>
  ky
    .post(`${AUTH_API}/projects/create`, getOptions({signalId: 'createProject', data: {name, description, aiContext}}))
    .json<ProjectCreationResponse>();

export const getProjectData = (projectId: string) =>
  ky.get(`${AUTH_API}/projects/${projectId}`, getOptions({signalId: 'getProjectData'})).json<Project>();

export const addTag = (projectId: string, tag: string) =>
  ky.put(`${AUTH_API}/projects/${projectId}/tags`, getOptions({signalId: 'addTag', data: {tag}})).json<Tag>();

export const deleteTag = (projectId: string, tag: string) =>
  ky.delete(`${AUTH_API}/projects/${projectId}/tags/${tag}`, getOptions({signalId: 'deleteTag'}));

export const removeUser = ({projectId, userId}: {projectId: string; userId: string}) =>
  ky.delete(`${AUTH_API}/projects/${projectId}/users/${userId}`, getOptions({signalId: 'removeUser'})).json<Project>();

export const inviteUsers = (projectId: string, users: User[]) =>
  ky
    .post(`${AUTH_API}/projects/${projectId}/users/invite`, getOptions({signalId: 'inviteUsers', data: {users}}))
    .json<InvitedUser[]>();

export const resendInvite = ({projectId, email}: {projectId: string; email: string}) =>
  ky
    .post(
      `${AUTH_API}/projects/${projectId}/users/resend-invite`,
      getOptions({signalId: 'resendInvite', data: {email}}),
    )
    .json();

export const setUserRole = ({projectId, userId, role}: {projectId: string; userId: string; role: UserRole}) =>
  ky
    .put(`${AUTH_API}/projects/${projectId}/role`, getOptions({signalId: 'setUserRole', data: {userId, role}}))
    .json<Project>();

export const getProjectMetadata = (projectId: string) =>
  ky
    .get(`${AUTH_API}/projects/${projectId}/metadata`, getOptions({signalId: 'getProjectMetadata'}))
    .json<ProjectMetadataResponse>();

export const updateProjectMetadata = (projectId: string, payload: ProjectMetadataPayload) =>
  ky
    .put(`${AUTH_API}/projects/${projectId}/metadata`, getOptions({signalId: 'updateProjectMetadata', data: payload}))
    .json<ProjectMetadataResponse>();

export const updateAiContext = (projectId: string, aiContext: AiContext) =>
  ky
    .put(`${AUTH_API}/projects/${projectId}/ai_context`, getOptions({signalId: 'updateAiContext', data: {aiContext}}))
    .json<Project>();

export const updateGeneralSettings = (projectId: string, payload: GeneralSettingsPayload) =>
  ky
    .put(
      `${AUTH_API}/projects/${projectId}/general_settings`,
      getOptions({signalId: 'updateGeneralSettings', data: payload}),
    )
    .json<Project>();

export const getInferenceMetaFields = (projectId: string) =>
  ky
    .get(`${AUTH_API}/projects/${projectId}/inference_meta_fields`, getOptions({signalId: 'getInferenceMetaFields'}))
    .json<InferenceMetaFieldsResponse>();
